// CTV-610: the background-video timeline editor. Each clip is a row on a shared
// 0..TIMELINE_SEC ruler; bars are laid contiguously (clip i+1 starts where clip
// i ends). Drag a bar's RIGHT EDGE to set its play length (ripples the clips
// after it, capped at the track and the clip's own footage); drag a bar's
// INTERIOR to slide which part of the source plays (its in-point). A playhead
// tracks the shared ClipPlayer's position; drag it / click the ruler to seek.
// Pure math lives in clipTimeline.ts.

import React from 'react';
import { ArrowUp, ArrowDown, X, GripHorizontal } from 'lucide-react';
import {
  TIMELINE_SEC,
  TIMELINE_SECTIONS,
  SECTION_CUTS,
  startAt,
  totalLength,
  clampLength,
  clampIn,
  snapCutLength,
  type TimelineClip,
} from './clipTimeline';
import { filmstripTransform } from './clipThumbnails';
import type { ClipPlayer } from './clipPlayer';

interface Props {
  clips: TimelineClip[];
  onChange: (clips: TimelineClip[]) => void;
  player: ClipPlayer;
}

type DragState =
  | { mode: 'resize' | 'pan'; index: number; startX: number; startValue: number; pxPerSec: number }
  | { mode: 'scrub'; rulerLeft: number; rulerWidth: number }
  | null;

// Left offset of the track region within the (full-width) wrapper: 144px label
// + 8px gap on the left, 68px controls + 8px gap on the right.
const TRACK_LEFT = 152;
const headLeft = (cumSec: number) =>
  `calc(${TRACK_LEFT}px + ${cumSec / TIMELINE_SEC} * (100% - 228px))`;

const ClipTimeline: React.FC<Props> = ({ clips, onChange, player }) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const rulerRef = React.useRef<HTMLDivElement>(null);
  const playheadRef = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef<DragState>(null);
  const clipsRef = React.useRef(clips);
  clipsRef.current = clips;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const playerRef = React.useRef(player);
  playerRef.current = player;

  const handlers = React.useRef<{ move: (e: PointerEvent) => void; up: () => void }>();
  if (!handlers.current) {
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      if (d.mode === 'scrub') {
        const rel = (e.clientX - d.rulerLeft) / d.rulerWidth;
        playerRef.current.seek(Math.max(0, Math.min(1, rel)) * TIMELINE_SEC);
        return;
      }
      const cs = clipsRef.current;
      const deltaSec = (e.clientX - d.startX) / d.pxPerSec;
      if (d.mode === 'resize') {
        const raw = clampLength(cs, d.index, d.startValue + deltaSec);
        // Snap the cut to a section boundary when it's close, then re-clamp.
        const len = clampLength(cs, d.index, snapCutLength(startAt(cs, d.index), raw));
        onChangeRef.current(cs.map((c, i) => (i === d.index ? { ...c, lengthSec: len } : c)));
      } else {
        const inSec = clampIn(cs, d.index, d.startValue + deltaSec);
        onChangeRef.current(cs.map((c, i) => (i === d.index ? { ...c, inSec } : c)));
      }
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    handlers.current = { move, up };
  }

  const bindWindow = () => {
    window.addEventListener('pointermove', handlers.current!.move);
    window.addEventListener('pointerup', handlers.current!.up);
  };

  const startBarDrag = (index: number, mode: 'resize' | 'pan', e: React.PointerEvent) => {
    e.preventDefault();
    const width = trackRef.current?.getBoundingClientRect().width ?? 1;
    const clip = clips[index];
    drag.current = {
      index,
      mode,
      startX: e.clientX,
      startValue: mode === 'resize' ? clip.lengthSec : clip.inSec,
      pxPerSec: width / TIMELINE_SEC,
    };
    bindWindow();
  };

  const startScrub = (e: React.PointerEvent) => {
    const rect = rulerRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.preventDefault();
    drag.current = { mode: 'scrub', rulerLeft: rect.left, rulerWidth: rect.width };
    const rel = (e.clientX - rect.left) / rect.width;
    player.seek(Math.max(0, Math.min(1, rel)) * TIMELINE_SEC);
    bindWindow();
  };

  // Keep the playhead in step with playback (driven through the DOM to avoid
  // re-rendering the whole timeline every animation frame).
  React.useEffect(
    () =>
      player.onPosition((cum) => {
        if (playheadRef.current) playheadRef.current.style.left = headLeft(cum);
      }),
    [player],
  );
  React.useEffect(
    () => () => {
      if (handlers.current) {
        window.removeEventListener('pointermove', handlers.current.move);
        window.removeEventListener('pointerup', handlers.current.up);
      }
    },
    [],
  );

  const moveRow = (index: number, dir: -1 | 1) => {
    const next = [...clips];
    const t = index + dir;
    if (t < 0 || t >= next.length) return;
    [next[index], next[t]] = [next[t], next[index]];
    onChange(next);
  };
  const remove = (index: number) => {
    URL.revokeObjectURL(clips[index].previewUrl);
    onChange(clips.filter((_, i) => i !== index));
  };

  const pct = (sec: number) => `${(sec / TIMELINE_SEC) * 100}%`;
  const marks = [0, 6, 12, 18, 24];

  return (
    <div className="space-y-2">
      <div className="relative">
        {/* Section labels — where the landing text choreography changes, so a
            clip cut aligned to a boundary lands on a scene transition. */}
        <div className="relative h-4 ml-[152px] mr-[76px]">
          {TIMELINE_SECTIONS.map((s, i) => {
            const end = TIMELINE_SECTIONS[i + 1]?.start ?? TIMELINE_SEC;
            return (
              <span
                key={s.start}
                className="absolute top-0 -translate-x-1/2 text-[9px] font-semibold uppercase tracking-wide text-violet-600 whitespace-nowrap"
                style={{ left: pct((s.start + end) / 2) }}
              >
                {s.label}
              </span>
            );
          })}
        </div>

        {/* Playhead — spans the ruler + all rows */}
        <div
          ref={playheadRef}
          aria-hidden
          className="absolute top-0 bottom-0 w-0.5 bg-rose-500 z-10 pointer-events-none"
          style={{ left: headLeft(0) }}
        >
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-rose-500 shadow" />
        </div>

        {/* Ruler — click / drag anywhere to move the playhead */}
        <div
          ref={rulerRef}
          onPointerDown={startScrub}
          className="relative h-5 ml-[152px] mr-[76px] cursor-pointer touch-none select-none"
          title="Click or drag to scrub"
        >
          {marks.map((m) => (
            <div
              key={m}
              className="absolute top-1 -translate-x-1/2 text-[10px] text-slate-400 tabular-nums"
              style={{ left: pct(m) }}
            >
              {m}s
            </div>
          ))}
        </div>

        {clips.map((clip, i) => {
          const start = startAt(clips, i);
          const canPan = clip.durationSec > clip.lengthSec + 0.05;
          const strip = filmstripTransform(clip.durationSec, clip.inSec, clip.lengthSec);
          return (
            <div key={clip.id} className="flex items-center gap-2 mt-1">
              {/* Label */}
              <div className="w-[144px] shrink-0 min-w-0">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700 truncate">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold shrink-0">
                    {i + 1}
                  </span>
                  <span className="truncate">{clip.name}</span>
                </div>
                <div className="text-[10px] text-slate-400 tabular-nums mt-0.5">
                  {clip.inSec.toFixed(1)}–{(clip.inSec + clip.lengthSec).toFixed(1)}s
                  {clip.durationSec > 0 && ` of ${clip.durationSec.toFixed(1)}s`}
                </div>
              </div>

              {/* Track */}
              <div
                ref={i === 0 ? trackRef : undefined}
                className="relative flex-1 h-11 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden"
              >
                {marks.slice(1, -1).map((m) => (
                  <div
                    key={m}
                    className="absolute top-0 bottom-0 w-px bg-slate-200"
                    style={{ left: pct(m) }}
                  />
                ))}
                {/* Bar wrapper: spans the clip's true [start, start+length]
                    window. The bar visual clips its filmstrip, so the resize
                    handle lives OUTSIDE it, straddling the right edge with a
                    fixed-size hit zone. A clip shortened to a sliver would
                    otherwise swallow its own handle and become impossible to
                    lengthen again (CTV-745). */}
                <div
                  className="absolute top-0 bottom-0"
                  style={{ left: pct(start), width: pct(clip.lengthSec) }}
                >
                  <div
                    className="absolute inset-0 rounded-md bg-slate-800 text-white select-none touch-none overflow-hidden"
                    onPointerDown={canPan ? (e) => startBarDrag(i, 'pan', e) : undefined}
                    title={canPan ? 'Drag to choose which part of the clip plays' : undefined}
                  >
                    {/* Filmstrip — all the clip's frames as one strip, slid so the
                        bar shows the current [in, in+length] window. Panning the
                        in-point slides the frames continuously. */}
                    {clip.thumbnails && clip.thumbnails.length > 0 && (
                      <div
                        className="absolute top-0 bottom-0 left-0 flex pointer-events-none"
                        style={{
                          width: `${strip.widthPct}%`,
                          transform: `translateX(${strip.translatePct}%)`,
                        }}
                      >
                        {clip.thumbnails.map((th, j) => (
                          <div
                            key={j}
                            className="h-full flex-1 bg-cover bg-center border-r border-black/25 last:border-r-0"
                            style={{ backgroundImage: `url(${th.url})` }}
                          />
                        ))}
                      </div>
                    )}
                    {/* Length label, above the filmstrip */}
                    <div className="absolute inset-0 flex items-center">
                      <span
                        className={
                          'ml-1 flex items-center gap-1 px-1 rounded bg-black/45 text-[10px] font-semibold tabular-nums truncate ' +
                          (canPan ? 'cursor-grab active:cursor-grabbing' : '')
                        }
                      >
                        {canPan && <GripHorizontal className="w-3 h-3 opacity-80 shrink-0" />}
                        {clip.lengthSec.toFixed(1)}s
                      </span>
                    </div>
                  </div>
                  {/* Resize handle: 16px hit zone centered on the bar's right
                      edge, constant regardless of how short the clip is. */}
                  <div
                    onPointerDown={(e) => startBarDrag(i, 'resize', e)}
                    className="group absolute top-0 bottom-0 right-0 w-4 translate-x-1/2 z-[1] flex items-center justify-center cursor-ew-resize touch-none"
                    title="Drag to change length"
                  >
                    <div className="h-7 w-1.5 rounded-full bg-white border border-slate-400 shadow-sm group-hover:border-slate-600 group-hover:bg-slate-50" />
                  </div>
                </div>
              </div>

              {/* Row controls */}
              <div className="flex items-center gap-0.5 shrink-0 w-[68px] justify-end">
                <button
                  type="button"
                  onClick={() => moveRow(i, -1)}
                  disabled={i === 0}
                  className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  aria-label={`Move clip ${i + 1} up`}
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveRow(i, 1)}
                  disabled={i === clips.length - 1}
                  className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  aria-label={`Move clip ${i + 1} down`}
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="p-1 rounded text-slate-400 hover:bg-rose-50 hover:text-rose-500"
                  aria-label={`Remove clip ${i + 1}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {/* Section-boundary guide lines (the clean cut points) — over the
            tracks, under the playhead. Snap targets when resizing a clip. */}
        {SECTION_CUTS.map((t) => (
          <div
            key={t}
            aria-hidden
            className="absolute top-4 bottom-0 border-l-2 border-dashed border-violet-500/60 pointer-events-none"
            style={{ left: headLeft(t) }}
          />
        ))}
      </div>

      {/* Total */}
      <div className="ml-[152px] mr-[76px] flex items-center justify-between text-[12px] pt-1">
        <span className="text-slate-600 tabular-nums">
          Total <span className="font-semibold text-slate-900">{totalLength(clips).toFixed(1)}s</span>{' '}
          / {TIMELINE_SEC}s
        </span>
        {TIMELINE_SEC - totalLength(clips) > 0.1 ? (
          <span className="text-amber-600 tabular-nums">
            {(TIMELINE_SEC - totalLength(clips)).toFixed(1)}s unfilled, holds the last frame.
          </span>
        ) : (
          <span className="text-emerald-600">Fills the full {TIMELINE_SEC}s.</span>
        )}
      </div>
    </div>
  );
};

export default ClipTimeline;
